import { CaretDown } from "@phosphor-icons/react"
import { type MergeWithBoxProps } from "@tokenterminal/ui/Box"
import {
  ListBox,
  Popover,
  Select as AriaSelect,
  SelectValue,
  type ListBoxProps,
  type ListBoxItemProps,
  type Key,
  type SelectValueProps,
} from "react-aria-components"
import { Button, type ButtonProps } from "../button/Button"
import { Icon } from "../icon"
import { ComboboxOption } from "./combobox/Option"
import { Field, type FieldProps } from "./Field"

const Option = ({
  children,
  ...props
}: MergeWithBoxProps<ListBoxItemProps<object>>) => {
  return <ComboboxOption {...props}>{children}</ComboboxOption>
}

type SelectFieldProps<T extends object> = Omit<FieldProps, "children"> & {
  children: ListBoxProps<T>["children"]
  isDisabled?: boolean
  isInvalid?: boolean
  items?: ListBoxProps<T>["items"]
  onSelectionChange?: (value: string) => void
  placeholder?: string
  selectedKey?: Key
  buttonProps?: Partial<ButtonProps>
  trigger?: (args: {
    selectValue: (props: SelectValueProps<T>) => React.ReactNode
  }) => React.ReactNode
}

/**
 * A form field that allows users to select an option from a list. If you want a searchable select field, use `Combobox` component instead.
 *
 * ```tsx
 * import { SelectField } from "@tokenterminal/core/ui/input/SelectField"
 *
 * <SelectField label="Favorite animal">
 *   <SelectField.Option>Aardvark</SelectField.Option>
 *   <SelectField.Option>Cat</SelectField.Option>
 *   <SelectField.Option>Dog</SelectField.Option>
 * </SelectField>
 * ```
 */

export const SelectField = <T extends object>({
  "aria-label": ariaLabel,
  children,
  description,
  errorMessage,
  isDisabled,
  isInvalid,
  items,
  label,
  placeholder = "Select an option",
  onSelectionChange,
  selectedKey,
  buttonProps,
  className,
  trigger,
}: SelectFieldProps<T>) => {
  return (
    <Field
      description={description}
      errorMessage={errorMessage}
      label={label}
      className={className}
    >
      <AriaSelect
        onSelectionChange={(val) => onSelectionChange?.(String(val))}
        selectedKey={selectedKey}
        aria-label={ariaLabel ?? label}
      >
        {trigger ? (
          trigger({
            selectValue: SelectValue,
          })
        ) : (
          <Button
            isDisabled={isDisabled}
            color={isInvalid ? "danger" : undefined}
            {...buttonProps}
          >
            <SelectValue>
              {({ defaultChildren, isPlaceholder }) => {
                return isPlaceholder ? placeholder : defaultChildren
              }}
            </SelectValue>
            <Icon as={CaretDown} />
          </Button>
        )}
        <Popover className="tt-popover">
          <ListBox className="data-[focused]:outline-none" items={items}>
            {children}
          </ListBox>
        </Popover>
      </AriaSelect>
    </Field>
  )
}

SelectField.Option = Option
